/*!
 * ev v0.2.4 
 * (c) 2019 dadajam4
 * Released under the MIT License.
 */
var EVListener = /** @class */ (function () {
    function EVListener(_a) {
        var context = _a.context, remover = _a.remover, type = _a.type, tag = _a.tag, handler = _a.handler, _b = _a.once, once = _b === void 0 ? false : _b;
        this.context = context;
        this._remover = remover;
        this.type = type;
        this.tag = tag;
        this.handler = handler;
        this.once = once;
    }
    /**
     * Remove this instance from parent context.
     */
    EVListener.prototype.remove = function () {
        if (!this._remover)
            return;
        this._remover(this);
        var self = this;
        self.context = null;
        self._remover = null;
        self.handler = null;
        self.tag = null;
        delete self.context;
        delete self._remover;
        delete self.handler;
        delete self.tag;
    };
    /**
     * Check match condition by type or handler or tag.
     * @param type
     * @param handler
     * @param tag
     */
    EVListener.prototype.match = function (type, handler, tag) {
        if (handler && handler !== this.handler)
            return false;
        if (type && this.type !== type) {
            return false;
        }
        if (tag && this.tag !== tag) {
            return false;
        }
        return true;
    };
    /**
     * Trigger this listener.
     * @param params
     */
    EVListener.prototype.trigger = function (params) {
        this.handler(params, this);
        if (this.once) {
            this.remove();
        }
    };
    return EVListener;
}());

/**
 * The context class for event operations.<br>
 * You can use as it is or inherit any classes in this class.<br>
 * <pre><code>const dv = new EV();
 * // or...
 * class SomeClass extends EV {}
 * </code></pre>
 * When using it with TypeScript, it is possible to strictly set type information by specifying an interface in Generics.<br>
 * <pre><code>class SomeClass extends EV<{event1: string, event2: boolean}> {
 *   constructor() {
 *     super();
 *     this.emit('event1', 5); // ng
 *     this.emit('event1', 'string'); // ok
 *     this.on('event2', event => {
 *       const var1: string = event; // ng
 *       const var2: boolean = event; // ok
 *     });
 *   }
 * }</code></pre>
 */
var EV = /** @class */ (function () {
    function EV() {
        this.__ev_listeners__ = [];
    }
    EV.prototype.on = function (typeOrOption, handlerOrOption, option, once) {
        var _this = this;
        var _type;
        var _handler;
        var _tag = undefined;
        var _once = false;
        if (typeof typeOrOption === 'object') {
            _type = typeOrOption.type;
            _handler = typeOrOption.handler;
            _tag = typeOrOption.tag;
            _once = !!typeOrOption.once;
        }
        else {
            _type = typeOrOption;
            if (typeof handlerOrOption === 'object') {
                _handler = handlerOrOption.handler;
                _tag = handlerOrOption.tag;
                _once = !!handlerOrOption.once;
            }
            else {
                _handler = handlerOrOption;
                if (option) {
                    _tag = option.tag;
                    _once = !!option.once;
                }
            }
        }
        var listener = new EVListener({
            context: this,
            remover: function (listener) {
                _this.__ev_listener_remover__(listener);
            },
            type: _type,
            tag: _tag,
            handler: _handler,
            once: once || _once,
        });
        this.__ev_listeners__.push(listener);
        return listener;
    };
    EV.prototype.once = function (typeOrOption, handlerOrOption, option) {
        return this.on(typeOrOption, handlerOrOption, option, true);
    };
    /**
     * Create and return listener instance by type, handler. This will be done immediately.
     * When registering this hook, do not expect the payload to be passed to the callback method.
     * @param type
     * @param handler
     */
    EV.prototype.immediate = function (type, handler, option) {
        handler();
        return this.on(type, handler, option);
    };
    EV.prototype.off = function (typeOrHandlerOrOption, handlerOrOption) {
        var _type = undefined;
        var _handler;
        var _tag = undefined;
        if (typeof typeOrHandlerOrOption === 'object') {
            _type = typeOrHandlerOrOption.type;
            _handler = typeOrHandlerOrOption.handler;
            _tag = typeOrHandlerOrOption.tag;
        }
        else if (typeof typeOrHandlerOrOption === 'function') {
            _handler = typeOrHandlerOrOption;
        }
        else {
            _type = typeOrHandlerOrOption;
            if (typeof handlerOrOption === 'object') {
                _handler = handlerOrOption.handler;
                _tag = handlerOrOption.tag;
            }
            else {
                _handler = handlerOrOption;
            }
        }
        var listeners = this.__ev_filter__(_type, _handler, _tag);
        listeners.forEach(function (listener) {
            listener.remove();
        });
    };
    /**
     * Remove all listener instances.
     */
    EV.prototype.offAll = function () {
        var listeners = this.__ev_listeners__.slice();
        listeners.forEach(function (listener) {
            listener.remove();
        });
    };
    /**
     * Emit event.
     * @param type
     * @param payload
     */
    EV.prototype.emit = function (type, payload) {
        var listeners = this.__ev_filter__(type);
        listeners.forEach(function (listener) {
            listener.trigger(payload);
        });
    };
    EV.prototype.__ev_listener_remover__ = function (listener) {
        this.__ev_listeners__.splice(this.__ev_listeners__.indexOf(listener), 1);
    };
    EV.prototype.__ev_filter__ = function (type, handler, tag) {
        return this.__ev_listeners__.filter(function (listener) {
            return listener.match(type, handler, tag);
        });
    };
    return EV;
}());

export default EV;
